import React, { useEffect, useState } from "react";
import GeoLocationSetter, { LocationValue } from "../geo/GeoLocationSetter";
import { StoreContact, getOrgStore, saveOrganizationContact, saveOrganizationLocationGeoIds } from "../../api/OrgAPI";
import { logApiError, getUserMessage } from "../../api/apiError";
import ChangePasswordPanel from "../ChangePasswordPanel";
import CloseOrganizationPanel from "./CloseOrganizationPanel";

interface ProfileProps {
    token: string;           // ✅ NEW: you need token to pass into LocationSelector and save endpoint
    onBack?: () => void;
    /** Authorities from the JWT; the close-organization panel is ORG_ROOT-only. */
    authorities?: string[];
    /** Called after the org account is closed (logs the user out). */
    onAccountClosed?: () => void;
}

// ✅ NEW: payload matches your backend DTO exactly
type OrgLocationGeoIdsPayload = {
    countryId: number;
    admin1Id: number;
    admin2Id: number;
    cityId: number;
    postalCode: number;      // ✅ this is postalCodeId (int), not the postal code string
    streetAddress1: string;
    streetAddress2: string;
};

const ProfileComponent: React.FC<ProfileProps> = ({ token, onBack, authorities, onAccountClosed }) => {
    const [location, setLocation] = useState<LocationValue | null>(null);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState<string | null>(null);

    // Public contact details, shown to searchers on every listing.
    const [contact, setContact] = useState<StoreContact>({ phone: "", email: "", website: "", whatsapp: "" });
    const [contactSaving, setContactSaving] = useState(false);
    const [contactMsg, setContactMsg] = useState<string | null>(null);

    useEffect(() => {
        getOrgStore(token)
            .then((store) => {
                if (store?.contact) {
                    setContact({
                        phone: store.contact.phone ?? "",
                        email: store.contact.email ?? "",
                        website: store.contact.website ?? "",
                        whatsapp: store.contact.whatsapp ?? "",
                    });
                }
            })
            .catch((e) => logApiError(e)); // prefill only — form still works
    }, [token]);

    const handleContactSave = async () => {
        setContactSaving(true);
        setContactMsg(null);
        try {
            await saveOrganizationContact(token, contact);
            setContactMsg("Contact details saved.");
        } catch (e) {
            logApiError(e);
            setContactMsg(getUserMessage(e));
        } finally {
            setContactSaving(false);
        }
    };

    const contactField = (label: string, key: keyof StoreContact, placeholder: string) => (
        <div className="form-group" style={{ marginBottom: "0.5rem" }}>
            <label>{label}</label>
            <input
                value={(contact[key] as string) ?? ""}
                onChange={(e) => { setContact((c) => ({ ...c, [key]: e.target.value })); setContactMsg(null); }}
                placeholder={placeholder}
                disabled={contactSaving}
            />
        </div>
    );

    const handleSave = async () => {
        setMsg(null);

        // Name exactly what's missing (labels match the dropdowns), instead
        // of one generic list the user has to cross-check field by field.
        const missing: string[] = [];
        if (!location?.country) missing.push("Country");
        if (!location?.admin1) missing.push("State / Region");
        if (!location?.admin2) missing.push("County / District");
        if (!location?.city) missing.push("City");
        if (!location?.postalCode) missing.push("Postal Code");
        if (!location?.streetAddress1?.trim()) missing.push("Street Address 1");
        if (missing.length > 0) {
            setMsg(`Still missing: ${missing.join(", ")}. Everything above Street Address 2 is required.`);
            return;
        }
        // Unreachable — the checks above covered it; narrows the types below.
        if (!location || !location.country || !location.admin1 || !location.admin2
                || !location.city || !location.postalCode || !location.streetAddress1) {
            return;
        }

        // ✅ Convert string IDs to numbers (your DTO uses int)
        const payload: OrgLocationGeoIdsPayload = {
            countryId: parseInt(String(location.country.id), 10),
            admin1Id: parseInt(String(location.admin1.id), 10),
            admin2Id: parseInt(String(location.admin2.id), 10),
            cityId: parseInt(String(location.city.id), 10),
            postalCode: parseInt(String(location.postalCode.id), 10),
            streetAddress1: location.streetAddress1.trim(),
            streetAddress2: (location.streetAddress2 ?? "").trim(),
        };

        if (
            Number.isNaN(payload.countryId) ||
            Number.isNaN(payload.admin1Id) ||
            Number.isNaN(payload.admin2Id) ||
            Number.isNaN(payload.cityId) ||
            Number.isNaN(payload.postalCode)
        ) {
            setMsg("One of the selected IDs is not a valid number.");
            return;
        }

        try {
            setSaving(true);

            await saveOrganizationLocationGeoIds(token, payload);

            setMsg("Location saved.");
        } catch (e) {
            logApiError(e);
            setMsg(getUserMessage(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="profile-container">
            <h2>Profile Settings</h2>

            <ChangePasswordPanel token={token} />

            <div className="section-box">
                <h4 style={{ marginBottom: "0.5rem" }}>Public Contact Details</h4>
                <div className="item-description" style={{ marginBottom: "0.75rem" }}>
                    Shown to searchers on every one of your listings (phone and email
                    appear only after they click “show”). Leave a field empty to not
                    offer that channel.
                </div>
                {contactField("Phone", "phone", "e.g. +1 512 555 0100")}
                {contactField("Email", "email", "e.g. contact@yourbusiness.com")}
                {contactField("Website", "website", "e.g. https://yourbusiness.com")}
                {contactField("WhatsApp", "whatsapp", "digits with country code, e.g. 15125550100")}
                <div className="button-group">
                    <button onClick={handleContactSave} disabled={contactSaving}>
                        {contactSaving ? "Saving…" : "Save Contact Details"}
                    </button>
                </div>
                {contactMsg && (
                    <p className={contactMsg === "Contact details saved." ? "success-message" : "error-message"} style={{ marginTop: "0.5rem" }}>
                        {contactMsg}
                    </p>
                )}
            </div>

            <div className="section-box">
                <h4 style={{ marginBottom: "1rem" }}>Location Information</h4>
                
                <GeoLocationSetter
                    token={token}
                    showSaveButton={false}
                    onChange={(value) => {
                        setLocation(value);
                        setMsg(null);
                    }}
                />

                <div className="button-group">
                    <button
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? "Saving…" : "Save Location"}
                    </button>

                    {onBack && (
                        <button
                            onClick={onBack}
                            className="btn-secondary"
                        >
                            Back
                        </button>
                    )}
                </div>

                {msg && (
                    <p className={msg === "Location saved." ? "success-message" : "error-message"} style={{ marginTop: "0.5rem" }}>
                        {msg}
                    </p>
                )}
            </div>

            {authorities?.includes("ROLE_ORG_ROOT") && (
                <CloseOrganizationPanel token={token} onClosed={onAccountClosed} />
            )}
        </div>
    );
};

export default ProfileComponent;